import React from 'react';
import { FileText } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Card, CardContent } from '../components/ui/card';

const TermsPage = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-slate-100 to-slate-50">
      <Header />

      <section className="py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-slate-900 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <FileText className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-slate-900 mb-4">
              Terms of Service
            </h1>
            <p className="text-slate-600">Last updated: January 2025</p>
          </div>

          <Card>
            <CardContent className="p-6 sm:p-8 space-y-6">
              <div>
                <h2 className="text-2xl font-bold text-slate-900 mb-3">1. Acceptance of Terms</h2>
                <p className="text-slate-600 leading-relaxed">
                  By accessing and using Peterhahn Fashion Coupon Club, you accept and agree to be bound by the terms and provisions of this agreement. If you do not agree to these terms, please do not use our service.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-slate-900 mb-3">2. Use of Service</h2>
                <p className="text-slate-600 leading-relaxed mb-3">
                  Peterhahn Fashion Coupon Club provides a platform for users to find and access coupon codes and deals from various retailers. You agree to use this service only for lawful purposes and in accordance with these Terms.
                </p>
                <p className="text-slate-600 leading-relaxed mb-3">
                  You agree NOT to:
                </p>
                <ul className="list-disc list-inside space-y-2 text-slate-600 pl-4">
                  <li>Use the service for any illegal or unauthorized purpose</li>
                  <li>Attempt to gain unauthorized access to any portion of the service</li>
                  <li>Copy, modify, or distribute any content from our website without permission</li>
                  <li>Use automated systems or software to extract data from the website</li>
                  <li>Interfere with or disrupt the service or servers</li>
                </ul>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-slate-900 mb-3">3. Coupon Codes and Deals</h2>
                <p className="text-slate-600 leading-relaxed mb-3">
                  Peterhahn Fashion Coupon Club aggregates coupon codes and deals from various sources. We make reasonable efforts to ensure the accuracy and validity of the information provided, however:
                </p>
                <ul className="list-disc list-inside space-y-2 text-slate-600 pl-4">
                  <li>We do not guarantee that all coupon codes will work</li>
                  <li>Coupons may expire or have terms and conditions set by the retailers</li>
                  <li>We are not responsible for any issues with coupon redemption</li>
                  <li>Savings amounts are estimates and may vary</li>
                </ul>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-slate-900 mb-3">4. Third-Party Links</h2>
                <p className="text-slate-600 leading-relaxed">
                  Our service contains links to third-party websites and retailers. These links are provided for your convenience, but we do not endorse or assume responsibility for the content, products, or services offered by these third parties. Your interactions with third-party sites are governed by their respective terms and policies.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-slate-900 mb-3">5. Intellectual Property</h2>
                <p className="text-slate-600 leading-relaxed">
                  All content on Peterhahn Fashion Coupon Club, including but not limited to text, graphics, logos, images, and software, is the property of Peterhahn Fashion Coupon Club or its content suppliers and is protected by intellectual property laws. You may not reproduce, distribute, or create derivative works without our express written permission.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-slate-900 mb-3">6. Disclaimer of Warranties</h2>
                <p className="text-slate-600 leading-relaxed">
                  Peterhahn Fashion Coupon Club is provided "as is" and "as available" without any warranties of any kind, either express or implied. We do not warrant that the service will be uninterrupted, secure, or error-free. We do not warrant the accuracy, completeness, or usefulness of any information on the service.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-slate-900 mb-3">7. Limitation of Liability</h2>
                <p className="text-slate-600 leading-relaxed">
                  To the fullest extent permitted by law, Peterhahn Fashion Coupon Club shall not be liable for any indirect, incidental, special, consequential, or punitive damages, or any loss of profits or revenues, whether incurred directly or indirectly, or any loss of data, use, goodwill, or other intangible losses resulting from:
                </p>
                <ul className="list-disc list-inside space-y-2 text-slate-600 pl-4 mt-3">
                  <li>Your access to or use of or inability to access or use the service</li>
                  <li>Any conduct or content of any third party on the service</li>
                  <li>Any content obtained from the service</li>
                  <li>Unauthorized access, use, or alteration of your data</li>
                </ul>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-slate-900 mb-3">8. Indemnification</h2>
                <p className="text-slate-600 leading-relaxed">
                  You agree to defend, indemnify, and hold harmless Peterhahn Fashion Coupon Club and its officers, directors, employees, and agents from any claims, damages, obligations, losses, liabilities, costs, or debt arising from your use of the service or violation of these Terms.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-slate-900 mb-3">9. Changes to Terms</h2>
                <p className="text-slate-600 leading-relaxed">
                  We reserve the right to modify or replace these Terms at any time. If a revision is material, we will provide at least 30 days notice prior to any new terms taking effect. Continued use of the service after changes constitutes acceptance of the new Terms.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-slate-900 mb-3">10. Governing Law</h2>
                <p className="text-slate-600 leading-relaxed">
                  These Terms shall be governed by and construed in accordance with the laws of the jurisdiction in which Peterhahn Fashion Coupon Club operates, without regard to its conflict of law provisions.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-slate-900 mb-3">11. Contact Information</h2>
                <p className="text-slate-600 leading-relaxed mb-4">
                  If you have any questions about these Terms of Service, please contact us:
                </p>
                <div className="bg-slate-100 rounded-lg p-4 space-y-2">
                  <p className="text-slate-700">
                    <strong>ASHISH KUMAR</strong>
                  </p>
                  <p className="text-slate-600">
                    FLAT 647, POCKET C4,<br />
                    SECTOR 34, ROHINI<br />
                    DELHI - 110039<br />
                    India (IN)
                  </p>
                  <p className="text-slate-600">
                    <strong>Developer:</strong> ivas.inc
                  </p>
                  <p className="text-slate-600">
                    <strong>Email:</strong>{' '}
                    <a href="mailto:tb930654@gmail.com" className="text-slate-900 hover:underline">
                      tb930654@gmail.com
                    </a>
                  </p>
                </div>
              </div>

              <div className="bg-slate-100 border-l-4 border-slate-900 p-4 rounded">
                <p className="text-sm text-slate-700">
                  <strong>Note:</strong> By using Peterhahn Fashion Coupon Club, you acknowledge that you have read, understood, and agree to be bound by these Terms of Service.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default TermsPage;
